import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Baby, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function JoinSync() {
  const [formData, setFormData] = useState({
    babyNameOrId: '',
    birthday: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, birthday: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, birthday: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.babyNameOrId.trim() || !formData.birthday) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      // Call backend to match baby by name/ID and birthday
      const response = await axios.post(`${API}/auth/join-sync`, {
        baby_name_or_id: formData.babyNameOrId.trim(),
        birthday: formData.birthday
      });

      const { baby, access_code, access_type } = response.data;
      
      // Store baby data in localStorage for full access (using correct key)
      localStorage.setItem('currentBaby', JSON.stringify(baby));
      // Mark this as a synced device to hide access management features
      localStorage.setItem('isSyncedDevice', 'true');
      
      toast.success(`Successfully synced with ${baby.name}'s records! You have full access.`);
      
      // Navigate to dashboard with full access
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
      
    } catch (error) {
      console.error('Error joining/syncing:', error);
      toast.error(error.response?.data?.detail || 'Unable to match details. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-pink-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <RefreshCw className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Join & Sync</CardTitle>
          <CardDescription>
            Sync your health records across devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="babyNameOrId">Name or ID</Label>
              <Input
                id="babyNameOrId"
                name="babyNameOrId"
                type="text"
                placeholder="Enter name or ID"
                value={formData.babyNameOrId}
                onChange={(e) => {
                  const value = e.target.value.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  ).join(' ');
                  setFormData(prev => ({ ...prev, babyNameOrId: value }));
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthday">Birthday</Label>
              <Input
                id="birthday"
                name="birthday"
                type="text"
                placeholder="DD/MM/YYYY"
                value={formData.birthday}
                onChange={handleDateInput}
                onKeyDown={handleDateKeyDown}
                maxLength={10}
                required
              />
              <p className="text-xs text-gray-500">
                Enter the same birthday as registered on the owner's device
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> Enter the name/ID and birthday. If they match the owner's device, you'll get synced access. The owner can manage your access from their device.
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Syncing...' : 'Join & Sync'}
            </Button>

            <div className="text-center pt-2 border-t">
              <button
                type="button"
                className="text-sm text-gray-600 hover:underline"
                onClick={() => navigate('/')}
              >
                Back to Login
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default JoinSync;
